/**
 * Sticky-notes auto-banner — fetches the customer's internal notes and
 * surfaces them prominently above the ticket. No-ops if notes are empty.
 *
 * Phase 5.8j (Customers P5).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, ExternalLink } from "lucide-react";

export default function CustomerStickyNotes({ customerId }) {
  const [notes, setNotes] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!customerId || customerId === "[gdpr-deleted]") return;
    let mounted = true;
    api.get(`/customers/${customerId}`)
      .then((r) => { if (mounted) { setNotes(r.data?.notes || ""); setLoaded(true); } })
      .catch(() => { if (mounted) setLoaded(true); });
    return () => { mounted = false; };
  }, [customerId]);

  if (!loaded || !notes) return null;

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-2.5"
         data-testid="ticket-sticky-notes-banner">
      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">
            Sticky notes on this customer
          </span>
          <Link to={`/customers/${customerId}`}
                className="text-[10px] uppercase tracking-wider text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"
                data-testid="sticky-notes-customer-link">
            View customer <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
        <div className="text-sm text-amber-900 mt-0.5 whitespace-pre-wrap">{notes}</div>
      </div>
    </div>
  );
}
